import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import './Budgets.css';
import './Budgets_CustomizeViews.css';
import MonthComparisonView from './MonthComparisonView';
import Rolling12MonthView from './Rolling12MonthView';
import QuarterComparisonView from './QuarterComparisonView';
import YearOverYearView from './YearOverYearView';

function Budgets() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [incomeList, setIncomeList] = useState([]);
  const [expenseList, setExpenseList] = useState([]);
  const [savingsList, setSavingsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({ income: true, expense: true, savings: true });
  const [viewMode, setViewMode] = useState('standard');
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [comparisonMonth1, setComparisonMonth1] = useState(new Date().getMonth());
  const [comparisonMonth2, setComparisonMonth2] = useState(new Date().getMonth() - 1 >= 0 ? new Date().getMonth() - 1 : 0);
  const user = auth.currentUser;

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  useEffect(() => {
    if (!user) return;
    
    setLoading(true);
    
    const loadIncomeCategories = async () => {
      const snapshot = await getDocs(collection(db, 'users', user.uid, 'income_categories'));
      return snapshot.docs
        .map(doc => doc.data().name?.trim())
        .filter(name => name)
        .sort();
    };
    
    const loadExpenseCategories = async () => {
      const snapshot = await getDocs(collection(db, 'users', user.uid, 'expense_categories'));
      return snapshot.docs
        .map(doc => doc.data().name?.trim())
        .filter(name => name)
        .sort();
    };
    
    const loadSavingsAccounts = async () => {
      const q = query(
        collection(db, 'users', user.uid, 'account_setup'),
        where('active', '==', true)
      );
      const snapshot = await getDocs(q);
      const accounts = [];
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const accountName = data.name || '';
        const accountGroup = data.group || '';
        const isSavings = accountGroup.toLowerCase().includes('saving');
        
        if (isSavings && accountName) {
          accounts.push(accountName);
        }
      });
      
      return accounts.sort();
    };
    
    Promise.all([
      loadIncomeCategories(),
      loadExpenseCategories(),
      loadSavingsAccounts()
    ])
      .then(([incomeCategories, expenseCategories, savingsAccounts]) => {
        const incomeItems = incomeCategories.map(name => ({
          category: name,
          budget: 0,
          actual: 0
        }));
        
        const expenseItems = expenseCategories.map(name => ({
          category: name,
          budget: 0,
          actual: 0
        }));
        
        const savingsItems = savingsAccounts.map(name => ({
          category: name,
          budget: 0,
          actual: 0
        }));
        
        setIncomeList(incomeItems);
        setExpenseList(expenseItems);
        setSavingsList(savingsItems);
        
        loadBudgetValues(incomeItems, expenseItems, savingsItems);
      })
      .catch(error => {
        console.error('Error loading categories:', error);
        setLoading(false);
      });
  }, [user, selectedYear, selectedMonth]);
  
  const loadBudgetValues = async (incomeItems, expenseItems, savingsItems) => {
    const monthStr = String(selectedMonth + 1).padStart(2, '0');
    const budgetDocId = `budget_${selectedYear}_${monthStr}`;
    
    try {
      const budgetDocRef = doc(db, 'users', user.uid, 'Budgets', budgetDocId);
      const docSnapshot = await getDoc(budgetDocRef);
      
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        
        if (data.income) {
          incomeItems.forEach(item => {
            const categoryData = data.income[item.category];
            if (categoryData) {
              item.budget = categoryData.budget || 0;
              item.actual = categoryData.actual || 0;
            }
          });
        }
        
        if (data.expense) {
          expenseItems.forEach(item => {
            const categoryData = data.expense[item.category];
            if (categoryData) {
              item.budget = categoryData.budget || 0;
              item.actual = categoryData.actual || 0;
            }
          });
        }
        
        if (data.savings) {
          savingsItems.forEach(item => {
            const categoryData = data.savings[item.category];
            if (categoryData) {
              item.budget = categoryData.budget || 0;
              item.actual = categoryData.actual || 0;
            }
          });
        }
      }
      
      setIncomeList([...incomeItems]);
      setExpenseList([...expenseItems]);
      setSavingsList([...savingsItems]);
      setLoading(false);
      
    } catch (error) {
      console.error('Error loading budget values:', error);
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleBudgetChange = (list, setList, index, value) => {
    const newList = [...list];
    newList[index].budget = parseFloat(value) || 0;
    setList(newList);
  };

  const calculateTotals = (list) => {
    let totalBudget = 0;
    let totalActual = 0;
    
    list.forEach(item => {
      totalBudget += item.budget || 0;
      totalActual += item.actual || 0;
    });
    
    return { budget: totalBudget, actual: totalActual };
  };

  const renderSection = (list, setList, sectionKey, title, colorClass, emoji) => {
    if (loading) {
      return (
        <div className="budget-section">
          <div className={`section-title ${colorClass}`} style={{ cursor: 'pointer' }}>
            <h3>{emoji} {title}</h3>
            <span className="section-total">Loading...</span>
          </div>
        </div>
      );
    }

    if (list.length === 0) {
      return (
        <div className="budget-section">
          <div className={`section-title ${colorClass}`} style={{ cursor: 'pointer' }} onClick={() => toggleSection(sectionKey)}>
            <h3>{emoji} {title}</h3>
            <span className="section-total">No data</span>
          </div>
        </div>
      );
    }

    const totals = calculateTotals(list);
    const difference = totals.actual - totals.budget;
    const isExpanded = expandedSections[sectionKey];

    return (
      <div className="budget-section">
        <div className={`section-title ${colorClass}`} style={{ cursor: 'pointer' }} onClick={() => toggleSection(sectionKey)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>{isExpanded ? '▼' : '▶'}</span>
            <h3>{emoji} {title}</h3>
          </div>
          <span className="section-total">
            Budget: £{Math.abs(totals.budget).toFixed(2)} | Actual: £{Math.abs(totals.actual).toFixed(2)}
          </span>
        </div>
        
        {isExpanded && (
          <>
            <table>
              <thead>
                <tr>
                  <th>CATEGORY</th>
                  <th className="right">BUDGET</th>
                  <th className="right">ACTUAL</th>
                  <th className="right">DIFFERENCE</th>
                  <th className="center">PROGRESS</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item, index) => {
                  const budget = Math.abs(item.budget || 0);
                  const actual = Math.abs(item.actual || 0);
                  const diff = budget - actual;
                  const percentage = budget > 0 ? Math.min((actual / budget) * 100, 100) : 0;
                  const isOver = actual > budget;
                  
                  return (
                    <tr key={index}>
                      <td><strong>{item.category}</strong></td>
                      <td className="right">
                        <input 
                          type="number" 
                          step="0.01"
                          value={Math.abs(item.budget) || 0}
                          onChange={(e) => handleBudgetChange(list, setList, index, e.target.value)}
                          style={{ width: '100px', padding: '4px', textAlign: 'right', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </td>
                      <td className="right">£{actual.toFixed(2)}</td>
                      <td className={`right ${diff >= 0 ? 'positive' : 'negative'}`}>
                        {diff >= 0 ? '+' : ''}£{Math.abs(diff).toFixed(2)}
                      </td>
                      <td className="center">
                        <div className="progress-bar">
                          <div 
                            className={`progress-fill ${isOver ? 'over' : ''}`} 
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                <tr className="summary-row">
                  <td><strong>TOTAL {title.toUpperCase()}</strong></td>
                  <td className="right">£{Math.abs(totals.budget).toFixed(2)}</td>
                  <td className="right">£{Math.abs(totals.actual).toFixed(2)}</td>
                  <td className={`right ${difference >= 0 ? 'positive' : 'negative'}`}>
                    {difference >= 0 ? '+' : ''}£{Math.abs(difference).toFixed(2)}
                  </td>
                  <td className="center">
                    {totals.budget > 0 ? Math.round((Math.abs(totals.actual) / Math.abs(totals.budget)) * 100) : 0}%
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{ marginTop: '15px', textAlign: 'right' }}>
              <button className="btn-primary" style={{ padding: '8px 20px' }}>
                Save Changes
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="budgets-container">
      <div className="budgets-header">
        <h2>Budgets</h2>
        <div className="header-controls">
          <button className="btn-customize" onClick={() => setShowCustomizeModal(true)}>
            📊 Customize View
          </button>
          <div className="year-selector">
            <button className="year-btn" onClick={() => setSelectedYear(selectedYear - 1)}>◀</button>
            <span className="year-display">{selectedYear}</span>
            <button className="year-btn" onClick={() => setSelectedYear(selectedYear + 1)}>▶</button>
          </div>
        </div>
      </div>
      
      {viewMode === 'standard' && (
        <>
          <div className="month-tabs">
            {months.map((month, index) => (
              <button
                key={month}
                className={`month-tab ${selectedMonth === index ? 'active' : ''}`}
                onClick={() => setSelectedMonth(index)}
              >
                {month}
              </button>
            ))}
          </div>
          
          {renderSection(incomeList, setIncomeList, 'income', 'Income', 'income', '💰')}
          {renderSection(savingsList, setSavingsList, 'savings', 'Savings', 'savings', '💎')}
          {renderSection(expenseList, setExpenseList, 'expense', 'Expenses', 'expense', '💳')}
        </>
      )}

      {viewMode === 'month-comparison' && (
        <MonthComparisonView 
          user={user}
          selectedYear={selectedYear}
          month1={comparisonMonth1}
          month2={comparisonMonth2}
          onMonthChange={(month, isFirst) => isFirst ? setComparisonMonth1(month) : setComparisonMonth2(month)}
          months={months}
        />
      )}

      {viewMode === 'rolling-12-month' && (
        <Rolling12MonthView 
          user={user}
          selectedYear={selectedYear}
          months={months}
        />
      )}

      {viewMode === 'quarter-comparison' && (
        <QuarterComparisonView 
          user={user}
          selectedYear={selectedYear}
          months={months}
        />
      )}

      {viewMode === 'year-over-year' && (
        <YearOverYearView 
          user={user}
          selectedYear={selectedYear}
          months={months}
        />
      )}

      {/* Customize Modal */}
      {showCustomizeModal && (
        <div className="modal-overlay" onClick={() => setShowCustomizeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Customize Budget View</h3>
              <button className="btn-close" onClick={() => setShowCustomizeModal(false)}>✕</button>
            </div>
            
            <div className="view-options">
              <div 
                className={`view-option ${viewMode === 'standard' ? 'active' : ''}`}
                onClick={() => { setViewMode('standard'); setShowCustomizeModal(false); }}
              >
                <div className="view-icon">📅</div>
                <h4>Standard Monthly View</h4>
                <p>View budget vs actual for a single month</p>
              </div>

              <div 
                className={`view-option ${viewMode === 'month-comparison' ? 'active' : ''}`}
                onClick={() => { setViewMode('month-comparison'); setShowCustomizeModal(false); }}
              >
                <div className="view-icon">⚖️</div>
                <h4>Month to Month Comparison</h4>
                <p>Compare two specific months side by side</p>
              </div>

              <div 
                className={`view-option ${viewMode === 'rolling-12-month' ? 'active' : ''}`}
                onClick={() => { setViewMode('rolling-12-month'); setShowCustomizeModal(false); }}
              >
                <div className="view-icon">📊</div>
                <h4>Rolling 12-Month View</h4>
                <p>View all 12 months with totals, averages & variance</p>
              </div>

              <div 
                className={`view-option ${viewMode === 'quarter-comparison' ? 'active' : ''}`}
                onClick={() => { setViewMode('quarter-comparison'); setShowCustomizeModal(false); }}
              >
                <div className="view-icon">📈</div>
                <h4>Quarterly Comparison</h4>
                <p>Compare Q1, Q2, Q3, Q4 performance</p>
              </div>

              <div 
                className={`view-option ${viewMode === 'year-over-year' ? 'active' : ''}`}
                onClick={() => { setViewMode('year-over-year'); setShowCustomizeModal(false); }}
              >
                <div className="view-icon">📉</div>
                <h4>Year-over-Year</h4>
                <p>Compare current year vs previous year</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Budgets;